type Options = {
  onKeyUp?: (event: KeyboardEvent) => void
  onKeyDown?: (event: KeyboardEvent) => void
  onKeyPress?: (event: KeyboardEvent) => void
  withControl?: boolean
  withShift?: boolean
  withAlt?: boolean
}

type SupportedEvent = 'keypress' | 'keyup' | 'keydown'

const useKeyboardListener = (key: KeyboardEvent['key'] | KeyboardEvent['key'][], options: Options) => {
  const hasPressedKey = (e: KeyboardEvent) => {
    const keyMatch = typeof key === 'string' ? e.key === key : key.includes(e.key)
    if (!keyMatch) return false
    if (options.withControl !== undefined && (e.ctrlKey || e.metaKey) !== options.withControl) return false
    if (options.withShift !== undefined && e.shiftKey !== options.withShift) return false
    if (options.withAlt !== undefined && e.altKey !== options.withAlt) return false
    return true
  }

  const isEditableTarget = () => {
    const el = document.activeElement
    if (!el) return false
    const tag = el.tagName.toLowerCase()
    return tag === 'input' || tag === 'textarea' || tag === 'select' || (el as HTMLElement).isContentEditable
  }

  const eventKeys: (keyof Options)[] = ['onKeyUp', 'onKeyDown', 'onKeyPress']

  const handle = (type: keyof Options) => (e: KeyboardEvent) => {
    if (!isEditableTarget() && hasPressedKey(e)) (options[type] as ((e: KeyboardEvent) => void))?.(e)
  }

  const listeners: Partial<Record<keyof Options, (e: KeyboardEvent) => void>> = {}

  for (const type of eventKeys) {
    if (options[type]) {
      listeners[type] = handle(type)
      globalThis.addEventListener(type.replace(/^on/, '').toLowerCase() as SupportedEvent, listeners[type]!)
    }
  }

  return () => {
    for (const type of eventKeys) {
      if (listeners[type]) {
        globalThis.removeEventListener(type.replace(/^on/, '').toLowerCase() as SupportedEvent, listeners[type]!)
      }
    }
  }
}

export default useKeyboardListener
